/**
 * "Quote Goat" is the internal platform name. A customer must never read it —
 * not on a page, not in a tab title, not in an email, not on a printed doc.
 *
 * Client-facing surfaces resolve their brand through here instead of falling
 * back to the platform name, in this order:
 *   1. the tenant name loaded from Supabase, when it has arrived
 *   2. the brand mapped to the current host
 *   3. Bliss Bedrooms — every client-facing deployment is Bliss today, so an
 *      unrecognized host (Vercel preview URL, new domain, localhost) shows the
 *      Bliss brand rather than leaking the platform name.
 */

import { isBlissBasicHost } from './blissBasicHost'
import { isBlissDesignHost } from './blissDesignHost'
import { tenantSlugForHost } from './hostTenants'

/** Internal only. Never render this to a customer. */
export const PLATFORM_INTERNAL_NAME = 'Quote Goat'

export const CLIENT_BRAND_BY_TENANT_SLUG: Record<string, string> = {
  'bliss-bedrooms': 'Bliss Bedrooms',
  'bliss-bunk-co': 'Bliss Bunk Beds',
  'renovation-flooring': 'Renovation Flooring',
  'beach-bumz': 'Beach Bumz',
}

export const DEFAULT_CLIENT_BRAND = 'Bliss Bedrooms'

/** The consumer-facing product line that sits under the Bliss Bedrooms brand. */
export const BLISS_PRODUCT_BRAND = 'Bliss Bunk Beds'

function currentHostname(hostname?: string | null): string {
  if (hostname) return hostname.toLowerCase()
  if (typeof window === 'undefined') return ''
  return window.location.hostname.toLowerCase()
}

/** Brand for the given host, ignoring any loaded tenant. */
export function clientBrandForHost(hostname?: string | null): string {
  const host = currentHostname(hostname)
  const slug = tenantSlugForHost(host)
  const mapped = slug ? CLIENT_BRAND_BY_TENANT_SLUG[slug] : undefined
  if (mapped) return mapped
  if (host.includes('bliss')) return BLISS_PRODUCT_BRAND
  return DEFAULT_CLIENT_BRAND
}

/**
 * Browser tab / social title for a host. The bunk business goes to market as
 * Bliss Bunk Beds, with Bliss Bedrooms as the parent brand, so the tab shows
 * the product line. Kept in sync with the inline script in index.html, which
 * sets the same title before the bundle loads.
 */
export function clientTabTitleForHost(hostname?: string | null): string {
  if (isBlissDesignHost(hostname)) return 'Plan Your Bunk Room'
  if (isBlissBasicHost(hostname)) return 'Bliss Basic'
  const brand = clientBrandForHost(hostname)
  return brand === DEFAULT_CLIENT_BRAND ? BLISS_PRODUCT_BRAND : brand
}

/**
 * Brand to show a customer. Pass whatever tenant name you have (it is often
 * null while Supabase is still loading, and on client approval pages the
 * tenant provider is skipped entirely).
 */
export function clientBrandName(...candidates: Array<string | null | undefined>): string {
  for (const candidate of candidates) {
    const name = candidate?.trim()
    if (!name) continue
    if (name.toLowerCase() === PLATFORM_INTERNAL_NAME.toLowerCase()) continue
    return name
  }
  return clientBrandForHost()
}
